import React, { useState, useEffect, useRef } from 'react';
import { Gift, Trophy, Sparkles, X, MonitorPlay, Play, RefreshCw } from 'lucide-react';
import api from '@/lib/api';

const DEMO_NAMES = ['Camila', 'Rodrigo', 'María José', 'Claudia', 'Francisco', 'Andrea', 'Pablo', 'Carolina'];

function SorteoPantalla({ participantes, resultado, ensayo, error, onClose }) {
  const [elapsed, setElapsed] = useState(0);
  const [nombre, setNombre] = useState('••••••••');
  const ref = useRef(null);
  const nombres = (ensayo ? DEMO_NAMES : participantes.map(p => p.nombre)).filter(Boolean);
  const revelado = elapsed >= 5000 && !!resultado;
  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => {
      const t = Date.now() - started;
      setElapsed(t);
      if (t < 5000 && nombres.length) setNombre(nombres[Math.floor(Math.random() * nombres.length)]);
    }, 105);
    return () => window.clearInterval(timer);
  }, []); // Mantener la lista inicial durante la animación

  useEffect(() => {
    if (ref.current?.requestFullscreen) ref.current.requestFullscreen().catch(() => {});
    return () => {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  const close = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    onClose();
  };
  const segundos = Math.max(1, Math.ceil((5000 - elapsed) / 1000));
  const mensaje = elapsed < 1200 ? 'REVISANDO PARTICIPANTES...' :
    elapsed < 2600 ? 'MEZCLANDO LA SUERTE...' :
    elapsed < 4000 ? '¡QUÉ NERVIOS!' : 'Y EL PREMIO ES PARA...';

  return <div ref={ref} role="dialog" aria-modal="true" aria-label="Sorteo en pantalla grande" className="fixed inset-0 z-[99999] bg-[#ff5a83] text-[#111] flex flex-col items-center justify-center overflow-hidden px-6 py-12">
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      <div className="absolute left-[-8%] bottom-[-15%] h-[35vw] w-[35vw] rounded-full bg-[#dfff00] opacity-65 blur-3xl" />
      <div className="absolute right-[-12%] top-[-25%] h-[40vw] w-[40vw] rounded-full bg-[#08d7ee] opacity-60 blur-3xl" />
    </div>
    <button type="button" onClick={close} className="absolute top-4 right-4 z-10 flex items-center gap-2 rounded-full bg-black/85 text-white px-4 py-2 text-sm font-bold"><X size={16}/> Salir</button>
    {ensayo && <span className="absolute top-4 left-4 z-10 rounded-full bg-black text-[#dfff00] px-4 py-2 text-sm font-black tracking-widest">MODO ENSAYO · NO SE GUARDA</span>}
    <div className="relative z-10 w-full max-w-6xl text-center flex flex-col items-center gap-5 sm:gap-7">
      <img src="/seniormarket-logos/seniormarket-brand-qr-transparente.png" alt="Senior Market" className="object-contain w-[210px] sm:w-[320px] max-h-[155px] sm:max-h-[200px]" />
      {error ? <>
        <h2 className="text-3xl sm:text-5xl font-black">NO PUDIMOS COMPLETAR EL SORTEO</h2>
        <p className="text-lg font-semibold">{error}</p>
        <button className="bg-black text-white px-7 py-3 rounded-2xl font-bold" onClick={close}>Volver al administrador</button>
      </> : revelado ? <>
        <div className="text-xl sm:text-4xl font-black tracking-widest animate-bounce">🎉 ¡TENEMOS GANADOR! 🎉</div>
        <div className="bg-white border-[5px] sm:border-[8px] border-black rounded-[32px] shadow-[12px_12px_0_#dfff00] px-7 sm:px-20 py-8 sm:py-12 w-full">
          <Trophy className="mx-auto mb-4 text-[#ff5a83] w-14 h-14 sm:w-20 sm:h-20" strokeWidth={2.8}/>
          <h2 className="font-black leading-[1.04] break-words text-[clamp(2.6rem,7vw,6.5rem)]">{resultado.nombre}</h2>
          <p className="mt-4 font-extrabold text-xl sm:text-3xl">{resultado.comuna || 'SeniorMarket'}</p>
        </div>
        <p className="text-base sm:text-2xl font-black">¡UN APLAUSO GIGANTE! 👏👏👏</p>
      </> : <>
        <span className="rounded-full bg-black text-[#dfff00] px-6 py-3 tracking-[.12em] text-base sm:text-2xl font-black">{mensaje}</span>
        <div className="w-full min-h-[190px] sm:min-h-[300px] flex items-center justify-center bg-white/90 border-[5px] border-black rounded-[32px] px-5 sm:px-10 shadow-[12px_12px_0_#dfff00]">
          <span className="font-black uppercase text-[clamp(2.2rem,6vw,6rem)] leading-tight break-words animate-pulse">{nombre}</span>
        </div>
        <div className="rounded-full bg-[#dfff00] border-4 border-black w-24 h-24 sm:w-36 sm:h-36 flex items-center justify-center shadow-lg">
          <span className="font-black text-5xl sm:text-7xl">{elapsed >= 5000 ? '🎁' : segundos}</span>
        </div>
        <p className="font-extrabold text-base sm:text-2xl">🎁 ¡La suerte está por decidir! 🎁</p>
      </>}
    </div>
  </div>;
}

export default function SeniorMarketRegistros() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draws, setDraws] = useState([]);
  const [winner, setWinner] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [pantalla, setPantalla] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [sorteoError, setSorteoError] = useState('');

  const refresh = async () => {
    setLoading(true); setError('');
    try {
      const [res, drawsRes] = await Promise.all([api.get('/seniormarket/registros'), api.get('/seniormarket/sorteos')]);
      setData(res.data.registros || []);
      setDraws(drawsRes.data.sorteos || []);
    } catch { setError('No se pudieron cargar los registros.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const download = async () => {
    try {
      const response = await api.get('/seniormarket/registros/csv', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href=url; a.download='seniormarket-registros.csv'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch { setError('No se pudo descargar el CSV.'); }
  };

  const iniciarSorteo = async (ensayo = false) => {
    if (drawing || pantalla) return;
    if (!ensayo && !window.confirm('¿Iniciar el sorteo REAL? El ganador quedará guardado y no podrá volver a ganar.')) return;
    setError(''); setSorteoError(''); setResultado(null); setWinner(null);
    // Solicitar pantalla completa desde el clic (gesto del usuario).
    if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    setPantalla(ensayo ? 'ensayo' : 'real');
    if (ensayo) {
      setResultado({ nombre: 'PARTICIPANTE DE PRUEBA', comuna: 'MODO ENSAYO' });
      return;
    }
    setDrawing(true);
    try {
      const res = await api.post('/seniormarket/sortear');
      setResultado(res.data.ganador);
      setWinner(res.data.ganador);
      const drawsRes = await api.get('/seniormarket/sorteos');
      setDraws(drawsRes.data.sorteos || []);
    } catch (e) {
      setSorteoError(e.response?.data?.detail || 'Error al sortear. Intenta nuevamente desde el administrador.');
    } finally { setDrawing(false); }
  };

  const disponibles = data.filter(p => !draws.some(d => d.correo === p.correo));
  return <div className="p-5 sm:p-7">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <div><h2 className="font-bold text-xl">Registros QR · SeniorMarket</h2><p className="text-sm text-gray-500">{data.length} participantes registrados</p></div>
      <div className="flex gap-2"><button onClick={refresh} className="px-4 py-2 border rounded-lg" disabled={loading}>{loading ? 'Cargando...' : 'Actualizar'}</button><button onClick={download} className="px-4 py-2 bg-[#111] text-white font-bold rounded-lg">Descargar Excel (CSV)</button></div>
    </div>
    {error && <p className="text-red-600 mb-3">{error}</p>}
    <section className="mb-5 p-5 rounded-2xl border-2 border-[#111] bg-[#dfff00]">
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <div><h3 className="font-black text-xl">🎁 Sorteo SeniorMarket</h3><p className="text-sm">¡Ahora con pantalla gigante, cuenta regresiva y revelación del ganador! {disponibles.length} participantes disponibles.</p></div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => iniciarSorteo(true)} className="rounded-xl border-2 border-[#111] bg-white px-4 py-3 font-bold flex items-center gap-2"><MonitorPlay size={20}/> Probar animación</button>
          <button type="button" onClick={() => iniciarSorteo(false)} disabled={drawing || !disponibles.length} className="rounded-xl bg-[#111] px-6 py-3 text-white font-black disabled:opacity-50 flex items-center gap-2"><Gift size={20}/>{drawing ? 'Sorteando...' : '🎉 Sortear ganador real'}</button>
        </div>
      </div>
      {winner && <div role="status" className="mt-4 p-5 bg-white rounded-xl border-2 border-black text-center"><p className="text-sm font-bold">¡ÚLTIMO GANADOR!</p><p className="text-2xl sm:text-3xl font-black my-2">{winner.nombre}</p><p>{winner.comuna} · {winner.correo} · {winner.telefono}</p></div>}
      {!!draws.length && <div className="mt-4"><h4 className="font-bold mb-2">Historial de ganadores ({draws.length})</h4><div className="space-y-2">{draws.map((d,i) => <div key={d.fecha_sorteo || i} className="bg-white/90 rounded-lg p-3 text-sm flex flex-wrap justify-between gap-2"><span><strong>{d.nombre}</strong> · {d.correo} · {d.telefono}</span><span>{new Date(d.fecha_sorteo).toLocaleString('es-CL')}</span></div>)}</div></div>}
    </section>
    <div className="overflow-x-auto"><table className="min-w-[690px] w-full text-sm">
      <thead><tr className="bg-gray-100 text-left">{['Nombre','Correo','Comuna','Edad','Teléfono','Fecha'].map(c => <th key={c} className="p-3">{c}</th>)}</tr></thead>
      <tbody>{data.map((p,i) => <tr key={p.correo || i} className="border-b">
        <td className="p-3">{p.nombre}</td><td className="p-3">{p.correo}</td><td className="p-3">{p.comuna}</td><td className="p-3">{p.edad}</td><td className="p-3">{p.telefono}</td><td className="p-3">{p.fecha_registro ? new Date(p.fecha_registro).toLocaleString('es-CL') : ''}</td>
      </tr>)}</tbody>
    </table>{!loading && !data.length && <p className="p-6 text-center text-gray-500">Todavía no hay registros.</p>}</div>
    {pantalla && <SorteoPantalla key={pantalla} participantes={disponibles} resultado={resultado} ensayo={pantalla === 'ensayo'} error={sorteoError} onClose={() => { setPantalla(null); setResultado(null); setSorteoError(''); }}/>}
  </div>;
}
